import { ApiClient, ApiError } from "./ApiClient";

// Contrato de la mutation cancelEDocument. Mantener sincronizado con
// amplify/functions/cancel-edocument/types.ts ("keep in sync").
export type CancelEDocumentType = "Invoice" | "Creditnote" | "Debitnote";

export type CancelEDocumentParams = {
    documentType: CancelEDocumentType;
    documentId: string;
    /** "manual" (default) para el offcanvas; "auto" solo lo usa la cola SQS. */
    mode?: "manual" | "auto";
    /**
     * false cuando el frontend va a crear una nota de crédito (que ya devuelve inventario),
     * para que la lambda no lo devuelva por partida doble. Default true.
     */
    restock?: boolean;
    reason?: string;
};

export type CancelEDocumentResponse = {
    ok: boolean;
    alreadyCancelled?: boolean;
    skipped?: boolean;
    document?: Record<string, unknown>;
};

const ERROR_MESSAGES: Record<string, string> = {
    VALIDATION: "El documento no es válido para anular",
    NOT_FOUND: "No se encontró el documento a anular",
    FORBIDDEN: "No tiene permiso para anular este documento",
    PARTIAL_FAILURE: "La anulación no se completó del todo, verifique el inventario",
    INTERNAL: "Error al anular el documento",
};

/**
 * Anulación transaccional de documentos electrónicos vía lambda (camino estándar).
 * La lambda marca el documento como anulado y revierte el inventario; el envío a
 * Hacienda de las notas asociadas sigue orquestándose desde el frontend.
 */
export async function cancelEDocumentViaLambda(
    params: CancelEDocumentParams,
): Promise<CancelEDocumentResponse> {
    const { data, errors } = await ApiClient.mutations.cancelEDocumentMutation({
        documentType: params.documentType,
        documentId: params.documentId,
        mode: params.mode ?? "manual",
        restock: params.restock ?? true,
        reason: params.reason,
    });

    if (errors?.length) {
        throw new ApiError(`Errores al anular ${params.documentType}`, "API_ERROR", errors);
    }
    if (!data?.ok) {
        const fallback = ERROR_MESSAGES[`${data?.errorCode}`] || ERROR_MESSAGES.INTERNAL;
        throw new ApiError(data?.errorMessage || fallback, "API_ERROR");
    }

    const document =
        typeof data.document === "string" ? JSON.parse(data.document) : data.document;
    return {
        ok: data.ok,
        alreadyCancelled: data.alreadyCancelled ?? undefined,
        skipped: data.skipped ?? undefined,
        document: (document || undefined) as Record<string, unknown> | undefined,
    };
}
